import React, { useState } from 'react';

/**
 * Data Sources, Methodology, and Disclaimer Component
 * Provides transparency on the data and logic used in the simulator.
 */
export default function DataSourcesDisclaimer() {
    const [isOpen, setIsOpen] = useState(false);

    const toggleOpen = () => setIsOpen(!isOpen);

    return (
        <div className={`disclaimer-container ${isOpen ? 'is-open' : ''}`}>
            <div className="disclaimer-header" onClick={toggleOpen}>
                <h3 className="disclaimer-title">
                    Data Sources, Methodology & Disclaimers
                </h3>
                <span className="disclaimer-toggle-icon">{isOpen ? '−' : '+'}</span>
            </div>

            {isOpen && (
                <div className="disclaimer-content">
                    <div className="disclaimer-section">
                        <h4 className="disclaimer-section-title">1. Data Sources</h4>
                        <ul className="disclaimer-list">
                            <li>
                                <strong>Income Base & Composition</strong>:
                                Source: Internal Revenue Service (IRS) Statistics of Income (SOI), Table 1.4 "Sources of Income by Size of Adjusted Gross Income," Tax Year 2021.
                                Used to establish baseline income totals (wages: $9.022T, self-employment: $517B, partnership: $470B, S-corp: $767B, capital gains: $2.049T, retirement: $1.266T) and income composition by bracket (e.g., $1M+ earners derive 42% from capital gains, 24% from business income).
                            </li>
                            {/* ... same content ... */}
                            <li>
                                <strong>Medicare Cost Per Beneficiary</strong>:
                                Source: 2024 Medicare Trustees Report, Appendix V.D, Table V.D1 "HI and SMI Average Incurred per Beneficiary Costs."
                                Baseline cost: $17,364 (Part A: $6,160, Part B: $8,531, Part D: $2,673). Age curve from MedPAC 2021 data (65-74: $12,230, 75-84: $16,140, 85+: $19,163).
                            </li>
                            <li>
                                <strong>National Health Expenditure (NHE) Projections (2023-2032)</strong>:
                                Source: Centers for Medicare & Medicaid Services (CMS), Office of the Actuary.
                                Used for baseline spending trajectories, growth rates (average ~5.4%/year), and category shares (Hospital, Physician, Drug, Admin).
                            </li>
                            <li>
                                <strong>Administrative Overhead Rates</strong>:
                                Source: CMS National Health Expenditure Accounts (NHEA) Table 20, 2024.
                                Private insurance: 10.7% ($176.5B / $1,644.6B). Medicare: 1.08% (Part B administrative expenses). Medicaid: 4.2% (MACPAC FY2024).
                            </li>
                            <li>
                                <strong>Drug Pricing - International Reference</strong>:
                                Source: RAND Corporation (IQVIA MIDAS 2018 data) and ASPE/HHS (2022 OECD comparators).
                                US brand drug prices: 3.22x-4.22x international reference prices (after rebates: ~3.22x). Biologics: 2.95x.
                            </li>
                            <li>
                                <strong>Federal Debt & Fiscal Outlook</strong>:
                                Source: Congressional Budget Office (CBO) Long-Term Budget Outlook.
                                Used for baseline federal debt-to-GDP ratios, deficit projections, and interest rate assumptions.
                            </li>
                            <li>
                                <strong>Healthcare Workforce Statistics</strong>:
                                Source: Bureau of Labor Statistics (BLS) Occupational Employment and Wage Statistics (OEWS).
                                Used for baseline employment figures, wage data for administrative workers, and sector distribution.
                            </li>
                            <li>
                                <strong>Medical Debt Stock</strong>:
                                Source: Kaiser Family Foundation (KFF) analysis, February 2024.
                                National medical debt: $220B. CFPB credit report data: $88B visible on credit reports.
                            </li>
                            <li>
                                <strong>Medical Education Debt</strong>:
                                Source: Association of American Medical Colleges (AAMC) "You Can Afford Medical School" and Fact Card (Class of 2024-2025).
                                Median debt per graduate: $215,000.
                            </li>
                        </ul>
                    </div>

                    <div className="disclaimer-section">
                        <h4 className="disclaimer-section-title">2. Methodology & Assumptions</h4>
                        <ul className="disclaimer-list">
                            <li>
                                <strong>Deterministic Microsimulation Model</strong>:
                                The SAFECARE Simulator uses a deterministic microsimulation approach similar to CBO and CMS methodologies.
                                It processes macroeconomic baselines year-by-year (2026-2039), applying policy levers to calculate revenues and spending deviations relative to baseline projections.
                            </li>
                            <li>
                                <strong>Baseline Projection Method</strong>:
                                <ul className="disclaimer-sublist">
                                    <li><em>Income Base</em>: IRS SOI TY2021 actual data projected forward using 3.3% nominal growth (2.5% real GDP growth + 0.8% CPI inflation)</li>
                                    <li><em>Health Spending</em>: CMS NHE baseline (2023-2032) with 5.4% average annual growth, extended to 2039</li>
                                    <li><em>Medicare Costs</em>: 2024 Trustees Report baseline ($17,364 per beneficiary) with demographic and medical inflation adjustments</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Revenue Calculation</strong>:
                                Total revenue = (Employer contributions) + (Individual contributions + Surtaxes) + (Self-employed contributions) + (Redirected federal spending) + (Copayments).
                                Surtax revenue sensitivity to capital coverage: 60% reduction factor based on IRS SOI income composition data.
                            </li>
                            <li>
                                <strong>Dynamic Scoring (Macroeconomic Feedback)</strong>:
                                GDP and unemployment impacts are estimated using literature-derived fiscal multipliers.
                                <ul className="disclaimer-sublist">
                                    <li><em>Health Spending Multiplier</em>: Reduced health spending frees capital for other sectors, with transition lag</li>
                                    <li><em>Fiscal Multiplier</em>: Federal deficit reduction has positive long-term impact on private investment (crowding-in effect)</li>
                                </ul>
                            </li>
                            <li>
                                <strong>Behavioral Adjustments</strong>:
                                The model assumes partial "leakage" in savings. For example, if "State Cooperation" is low (50%), administrative savings are penalized by 50% to reflect friction and non-compliance.
                            </li>
                            <li>
                                <strong>Redistribution Logic</strong>:
                                Household savings are calculated by comparing SAFECARE contribution costs (Payroll + Income Tax) vs. Baseline costs (Premiums + OOP + Deductibles). This determines "net savings" per income decile.
                            </li>
                        </ul>
                    </div>

                    <div className="disclaimer-section last">
                        <h4 className="disclaimer-section-title">3. Disclaimer</h4>
                        <p className="disclaimer-text">
                            This simulator is an educational tool designed to model the <em>potential</em> impacts of the SAFECARE Act based on user-configured scenarios.
                            <strong>It is not a guaranteed prediction of the future.</strong>
                        </p>
                        <p className="disclaimer-text">
                            Economic modeling involves inherent uncertainties. Actual outcomes would depend on implementation details, global economic conditions, and behavioral responses not fully captured here.
                            This tool should not be used for financial planning or investment advice.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
